import React, { ReactElement, useContext, useEffect } from 'react';

import {
  insertAtIndex,
  removeById,
  reorder,
  sortByOrder,
  updateById,
} from '@OnArk/utils/arrayHelpers';
import useRouteState from '@OnArk/utils/useRouteState';

interface OrderableContextValue<D> {
  addItem: (item: D, insertAt?: number) => D[];
  items: D[];
  onItemMove?: (from: number, to: number) => void;
  removeItem: (id: string) => void;
  updateItem: (item: D) => void;
  setItems: React.Dispatch<
    React.SetStateAction<
      (D & {
        order?: Maybe<number> | undefined;
        id: string;
      })[]
    >
  >;
}
const OrderableContext = React.createContext({} as OrderableContextValue<any>);

export const useOrderableContext = <D,>(): OrderableContextValue<D> => {
  const contextValue = useContext(OrderableContext);

  return {
    ...contextValue,
  };
};

export function OrderableListContext<
  D extends { order?: Maybe<number>; id: string },
>({
  children,
  items: ITEMS,
  saveNewOrder,
}: React.PropsWithChildren<{
  items: D[];
  saveNewOrder: (items: D[], initial: D[]) => Promise<void> | void;
}>): ReactElement {
  const [orderedItems, setItems] = useRouteState(sortByOrder(ITEMS));

  useEffect(() => {
    setItems(ITEMS);
  }, [ITEMS]);

  const addItem = (item: D, insertAt = orderedItems.length) => {
    const newItems = insertAtIndex(orderedItems, insertAt, item);

    void saveNewOrder(newItems, orderedItems);
    setItems(newItems);

    return newItems;
  };

  const onItemMove = (from: number, to: number) => {
    const newItems = reorder(orderedItems, from, to);

    void saveNewOrder(newItems, orderedItems);
    setItems(newItems);
  };

  const removeItem = (id: string) => {
    const newItems = removeById(orderedItems, id);

    setItems(newItems);
    void saveNewOrder(newItems, orderedItems);
  };

  const updateItem = (item: D) => {
    const newItems = updateById(orderedItems, item);

    setItems(newItems);
  };

  return (
    <OrderableContext.Provider
      value={{
        addItem,
        items: orderedItems,
        onItemMove,
        removeItem,
        setItems,
        updateItem,
      }}
    >
      {children}
    </OrderableContext.Provider>
  );
}
